import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection, onSnapshot, getDocs, getDoc, doc, setDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import HeaderActionsPortal from './HeaderActionsPortal';
import {
  Armchair, Users, Plus, X, Pencil, Trash2, Shuffle, Eraser, ChevronLeft, DoorOpen,
  Presentation, SquareDashed, Search, LayoutGrid, AlertTriangle, Copy, Printer,
} from 'lucide-react';
import {
  CELL, cellKey, cellTypeAt, nextCellType, buildGrid, unseatedStudents, strandedSeats,
  seatStudent, clearSeat, swapSeats, autoAssign, resizeGrid, doorSlots, isDoorAt,
  planSummary, byUpdatedDesc, pruneSeats,
  hasColGap, hasRowGap, toggleColGap, toggleRowGap, pairColumns, noGaps,
} from '../seatingPlan';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

/**
 * Seating Plan -- where a class sits, room by room.
 *
 * A plan is a grid of cells rather than a list of seats, because a real room is a rectangle with
 * things missing: an aisle, a pillar, the teacher's desk. Clicking a cell walks it through seat ->
 * teacher's desk -> blocked, which is the whole of room setup.
 *
 * GROUPS EXIST BECAUSE PLENTY OF LESSONS ARE NOT THE WHOLE CLASS. An elective, a split set, a
 * remedial group -- each needs its own seating and none of them is a class. A group is a named list
 * of students that appears in the same dropdown as the classes, so choosing one is the same act as
 * choosing a class rather than a second way of doing it.
 *
 * The plan owns the seating; nothing is written back onto the student. So a class can have four
 * plans for four subjects without any of them contradicting the roll.
 */

const card = (isMobile) => ({
  backgroundColor: 'var(--color-bg-card)',
  borderRadius: isMobile ? 0 : 'var(--radius-lg)',
  boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  overflow: 'hidden',
});

const th = { padding: '0.5rem 0.7rem', fontWeight: '600', fontSize: '0.75rem', color: 'var(--color-text-subtle)', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '0.5rem 0.7rem', borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' };
const rowBtn = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: '0.2rem' };
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.1rem 0.5rem',
  borderRadius: '999px', fontSize: '0.72rem', fontWeight: '600',
  backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-subtle)',
};
const selectStyle = { padding: '0.45rem 0.6rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.85rem', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-main)' };
const modalBtn = { width: 'auto', padding: '0.55rem 1.1rem' };
const barBtn = { width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.3rem' };
const sizeStep = {
  flexShrink: 0, width: '1.9rem', padding: 0, cursor: 'pointer',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-main)',
  fontSize: '1rem', fontWeight: '700', lineHeight: 1,
};

const nowIso = () => new Date().toISOString();

const emptyPlan = () => ({
  name: '', sourceType: 'class', sourceId: '', sourceLabel: '',
  rows: 5, cols: 6, cells: {}, seats: {}, colGaps: {}, rowGaps: {},
  door: { edge: 'bottom', index: 0 },
});

// An aisle is a walkway, not a desk -- narrow on purpose. The handle is what you click in Room
// setup to put one there; it takes up a little space so it can be hit, and none at all once you
// leave setup.
const AISLE_W = '1.35rem';
const HANDLE_W = '0.55rem';

const emptyGroup = () => ({ name: '', classCode: '', students: [] });

const SeatingPlan = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile, isMobile, closeSidebar } = useOutletContext();
  const [activeTab, setActiveTab] = useState('plans');

  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownName = userProfile?.name || userProfile?.initial || ownEmail;

  // --- Reference data -------------------------------------------------------------------------
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  useEffect(() => {
    if (!activeSchoolId) return;
    setLoadingRefs(true);
    (async () => {
      try {
        const sysSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
        const dropdowns = sysSnap.exists() ? (sysSnap.data().dropdowns || {}) : {};
        setClasses((dropdowns.classes || []).filter(c => c.active !== false));
      } catch (e) { console.error('Error loading classes', e); }

      try {
        const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          list.push({
            email: d.id,
            sid: data.sid,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown',
            currentClass: data.currentClass || '',
            classNumber: Number(data.classNumber) || 0,
          });
        });
        list.sort((a, b) => String(a.currentClass).localeCompare(String(b.currentClass), undefined, { numeric: true })
          || a.classNumber - b.classNumber || a.name.localeCompare(b.name));
        setStudents(list);
      } catch (e) { console.error('Error loading students', e); }
      setLoadingRefs(false);
    })();
  }, [activeSchoolId]);

  // --- Groups ---------------------------------------------------------------------------------
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!activeSchoolId) return undefined;
    const unsub = onSnapshot(
      collection(db, 'schools', activeSchoolId, 'seating_group'),
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true }));
        setGroups(list);
      },
      (e) => console.error('Error loading seating groups', e),
    );
    return () => unsub();
  }, [activeSchoolId]);

  // --- Plans ----------------------------------------------------------------------------------
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    if (!activeSchoolId) return undefined;
    setLoadingPlans(true);
    const unsub = onSnapshot(
      collection(db, 'schools', activeSchoolId, 'seating_plan'),
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        setPlans(list.sort(byUpdatedDesc));
        setLoadingPlans(false);
      },
      (e) => { console.error('Error loading seating plans', e); setLoadingPlans(false); },
    );
    return () => unsub();
  }, [activeSchoolId]);

  // The one dropdown a plan is built from. Classes and groups sit in the same list on purpose:
  // choosing a group is the same act as choosing a class, not a second way of doing it.
  const sources = useMemo(() => ([
    ...classes.map(c => ({ type: 'class', id: c.code, label: c.name || c.code })),
    ...groups.map(g => ({ type: 'group', id: g.id, label: `${g.name} (group)` })),
  ]), [classes, groups]);

  const rosterFor = useMemo(() => (sourceType, sourceId) => {
    if (sourceType === 'group') {
      const g = groups.find(x => x.id === sourceId);
      return (g?.students || []).slice().sort((a, b) => (Number(a.classNumber) || 0) - (Number(b.classNumber) || 0));
    }
    return students.filter(s => s.currentClass === sourceId);
  }, [groups, students]);

  const [sourceFilter, setSourceFilter] = useState('');
  const shownPlans = useMemo(
    () => (sourceFilter ? plans.filter(p => `${p.sourceType}:${p.sourceId}` === sourceFilter) : plans),
    [plans, sourceFilter],
  );

  // --- The open plan --------------------------------------------------------------------------
  const [openId, setOpenId] = useState(null);        // null = the list
  const [draft, setDraft] = useState(null);          // the plan being edited
  const [mode, setMode] = useState('arrange');       // 'room' | 'arrange'
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [picked, setPicked] = useState(null);        // the student or seat waiting for a tap
  const [ghostNotice, setGhostNotice] = useState(0); // students recovered from a broken save

  const roster = useMemo(
    () => (draft ? rosterFor(draft.sourceType, draft.sourceId) : []),
    [draft, rosterFor],
  );
  const unseated = useMemo(() => unseatedStudents(roster, draft), [roster, draft]);
  const stranded = useMemo(() => strandedSeats(roster, draft), [roster, draft]);
  const summary = useMemo(() => planSummary(draft, roster), [draft, roster]);
  const grid = useMemo(() => buildGrid(draft), [draft]);

  const openPlan = (plan) => {
    if (closeSidebar) closeSidebar();
    const loaded = {
      ...emptyPlan(),
      ...plan,
      cells: plan.cells || {},
      seats: plan.seats || {},
      colGaps: plan.colGaps || {},
      rowGaps: plan.rowGaps || {},
      door: plan.door || { edge: 'bottom', index: 0 },
    };
    // A plan saved by an earlier version can carry students in cells that are not seats. They are
    // drawn nowhere and counted as seated, so they simply vanish. Healed on the way in: they come
    // back as unseated, which is visible and true. Marked dirty so the repair gets saved.
    const { seats, dropped } = pruneSeats(loaded);
    setOpenId(plan.id);
    setDraft({ ...loaded, seats });
    setMode('arrange');
    setPicked(null);
    setSizeDraft({ rows: '', cols: '' });
    setDirty(dropped > 0);
    if (dropped > 0) {
      setGhostNotice(dropped);
    } else {
      setGhostNotice(0);
    }
  };

  const closePlan = () => {
    if (dirty && !window.confirm(t('seatingPlan.leaveWithoutSaving'))) return;
    setOpenId(null); setDraft(null); setPicked(null); setDirty(false);
  };

  // Takes a function of the previous draft, never a snapshot of the current one. Clicking six
  // cells quickly is one React batch: every handler that read `draft` from its closure would be
  // reading the state as it was before the batch started, so all but the last click would be
  // thrown away -- which is how seats stopped being cleared when a cell became a teacher desk.
  const patch = (fn) => {
    setDraft(p => ({ ...p, ...(typeof fn === 'function' ? fn(p) : fn) }));
    setDirty(true);
  };

  // --- New plan dialog ------------------------------------------------------------------------
  const [newForm, setNewForm] = useState(null);
  const [creating, setCreating] = useState(false);

  const openNew = () => {
    if (closeSidebar) closeSidebar();
    setNewForm({ ...emptyPlan(), sourceKey: sources[0] ? `${sources[0].type}:${sources[0].id}` : '' });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (creating || !newForm?.sourceKey) return;
    setCreating(true);
    try {
      const [type, ...rest] = newForm.sourceKey.split(':');
      const id = rest.join(':');
      const src = sources.find(s => s.type === type && s.id === id);
      const docId = doc(collection(db, 'schools', activeSchoolId, 'seating_plan')).id;
      const body = {
        name: newForm.name.trim() || `${src?.label || id} seating`,
        sourceType: type,
        sourceId: id,
        sourceLabel: src?.label || id,
        rows: Math.max(1, Number(newForm.rows) || 1),
        cols: Math.max(1, Number(newForm.cols) || 1),
        cells: {},
        seats: {},
        colGaps: {},
        rowGaps: {},
        door: { edge: 'bottom', index: 0 },
        createdByEmail: ownEmail,
        createdByName: ownName,
        updatedOn: nowIso(),
        updatedAt: serverTimestamp(),
        updatedByEmail: ownEmail,
      };
      await setDoc(doc(db, 'schools', activeSchoolId, 'seating_plan', docId), body);
      setNewForm(null);
      // Straight into the room, because a plan with no seats arranged is not finished.
      openPlan({ id: docId, ...body });
    } catch (err) {
      console.error('Error creating seating plan', err);
      alert(t('seatingPlan.createFailed', { message: err.message }));
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!draft || saving) return;
    setSaving(true);
    try {
      // Pruned again on the way out, so a plan can never be written with a student sitting in a
      // cell that is not a seat.
      const { seats: cleanSeats } = pruneSeats(draft);

      // WRITTEN WHOLE, NOT MERGED. `cells`, `seats`, `colGaps` and `rowGaps` are maps, and
      // `setDoc(..., { merge: true })` merges maps KEY BY KEY -- it can add a key and change one,
      // but it can never remove one. So every deletion was thrown away on save:
      //
      //   * turning a teacher's desk back into an ordinary seat did not stick
      //   * moving a student left a copy behind in the seat they came from, in a cell the grid no
      //     longer draws them in -- which is exactly how students went missing
      //
      // Both faults look like "it reverted after I saved", because that is what it was. The whole
      // document is written instead, so what is stored is what the editor holds. createdBy* are
      // carried through from the loaded plan rather than being dropped by the replace.
      await setDoc(doc(db, 'schools', activeSchoolId, 'seating_plan', openId), {
        name: draft.name,
        sourceType: draft.sourceType,
        sourceId: draft.sourceId,
        sourceLabel: draft.sourceLabel,
        rows: draft.rows,
        cols: draft.cols,
        cells: draft.cells || {},
        seats: cleanSeats,
        colGaps: draft.colGaps || {},
        rowGaps: draft.rowGaps || {},
        door: draft.door,
        createdByEmail: draft.createdByEmail || ownEmail,
        createdByName: draft.createdByName || ownName,
        updatedOn: nowIso(),
        updatedAt: serverTimestamp(),
        updatedByEmail: ownEmail,
      });
      setDraft(p => ({ ...p, seats: cleanSeats }));
      setGhostNotice(0);
      setDirty(false);
    } catch (err) {
      console.error('Error saving seating plan', err);
      alert(t('seatingPlan.saveFailed', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  // Room setup draws thin handles between the columns for you to click. They mean nothing on
  // paper, so printing leaves setup first -- and a React state change has to be painted before the
  // print dialog reads the page, hence the frame wait rather than calling print() straight away.
  const handlePrint = () => {
    setMode('arrange');
    setPicked(null);
    requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
  };

  const handleDeletePlan = async (plan) => {
    const filled = Object.keys(plan.seats || {}).length;
    if (!window.confirm(t('seatingPlan.deletePlanConfirm', { name: plan.name, count: filled }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'seating_plan', plan.id));
      if (openId === plan.id) { setOpenId(null); setDraft(null); }
    } catch (err) {
      console.error('Error deleting seating plan', err);
      alert(t('seatingPlan.deleteFailed', { message: err.message }));
    }
  };

  const handleDuplicate = async (plan) => {
    try {
      const docId = doc(collection(db, 'schools', activeSchoolId, 'seating_plan')).id;
      // `id` is the document's name, not a field on it. Spreading the plan and setting it to
      // undefined does not remove it -- Firestore rejects an undefined value outright, so the whole
      // copy failed. Destructured away instead.
      const { id: _ignored, ...body } = plan;
      await setDoc(doc(db, 'schools', activeSchoolId, 'seating_plan', docId), {
        ...body,
        name: `${plan.name} (copy)`,
        createdByEmail: ownEmail,
        createdByName: ownName,
        updatedOn: nowIso(),
        updatedAt: serverTimestamp(),
        updatedByEmail: ownEmail,
      });
    } catch (err) {
      console.error('Error duplicating seating plan', err);
      alert(t('seatingPlan.duplicateFailed', { message: err.message }));
    }
  };

  // --- Room setup -----------------------------------------------------------------------------
  // The size fields keep their own text while you are typing, and only resize the room when you
  // leave the field or press Enter.
  //
  // Resizing on every keystroke made them impossible to use. Going from 5 to 12 means passing
  // through "1", and "1" is a one-row room: the grid collapsed, twenty-odd students were displaced,
  // and a confirm dialog appeared -- after a single digit. Clearing the field first was worse,
  // because an empty box reads as 0 and snapped straight to 1. Either way the number could never be
  // changed. The commit boundary is what fixes it; the confirmation still guards the real shrink.
  const [sizeDraft, setSizeDraft] = useState({ rows: '', cols: '' });

  const commitSize = (which) => {
    const typed = sizeDraft[which];
    setSizeDraft(d => ({ ...d, [which]: '' }));
    if (typed === '') return;                       // left untouched, or cleared and abandoned
    const n = Math.round(Number(typed));
    if (!Number.isFinite(n) || n < 1) return;       // nonsense keeps the room it had
    const rows = which === 'rows' ? n : draft.rows;
    const cols = which === 'cols' ? n : draft.cols;
    if (rows === draft.rows && cols === draft.cols) return;

    const next = resizeGrid(draft, rows, cols);
    if (next.displaced > 0
      && !window.confirm(t('seatingPlan.smallerRoomConfirm', { count: next.displaced }))) {
      return;
    }
    patch(p => {
      const { displaced, ...rest } = resizeGrid(p, rows, cols);
      return rest;
    });
  };

  // The +/- buttons commit at once: one press is already a complete instruction, so there is
  // nothing to wait for.
  const stepSize = (which, by) => {
    const rows = which === 'rows' ? Math.max(1, draft.rows + by) : draft.rows;
    const cols = which === 'cols' ? Math.max(1, draft.cols + by) : draft.cols;
    if (rows === draft.rows && cols === draft.cols) return;
    const next = resizeGrid(draft, rows, cols);
    if (next.displaced > 0
      && !window.confirm(t('seatingPlan.smallerRoomConfirm', { count: next.displaced }))) {
      return;
    }
    patch(p => {
      const { displaced, ...rest } = resizeGrid(p, rows, cols);
      return rest;
    });
  };

  const cycleCell = (row, col) => {
    patch(p => {
      const key = cellKey(row, col);
      const type = nextCellType(cellTypeAt(p, row, col));
      const cells = { ...p.cells };
      if (type === CELL.SEAT) delete cells[key]; else cells[key] = type;
      // A cell that stops being a seat cannot keep the student who was in it.
      return { cells, seats: type === CELL.SEAT ? p.seats : clearSeat(p.seats, key) };
    });
  };

  // --- Arranging ------------------------------------------------------------------------------
  //
  // Tap-then-tap is the primary gesture, not a fallback: HTML5 drag events never fire on a touch
  // screen and this runs as an installed app on tablets. Dragging is layered on top for a mouse.
  const takeStudent = (student) => setPicked({ kind: 'student', student });
  const takeSeat = (key) => {
    const sitting = draft.seats?.[key];
    if (!sitting) return;
    setPicked({ kind: 'seat', key, student: sitting });
  };

  const dropOn = (key) => {
    if (!picked) { takeSeat(key); return; }
    if (picked.kind === 'seat') {
      patch(p => ({ seats: swapSeats(p.seats, picked.key, key) }));
    } else {
      const who = {
        email: picked.student.email, sid: picked.student.sid || '', name: picked.student.name || '',
        currentClass: picked.student.currentClass || '', classNumber: picked.student.classNumber || '',
      };
      patch(p => ({ seats: seatStudent(p.seats, key, who) }));
    }
    setPicked(null);
  };

  const handleAuto = () => {
    const { seats, placed, leftOver } = autoAssign(draft, roster);
    patch(() => ({ seats }));
    if (leftOver > 0) {
      alert(t('seatingPlan.autoLeftOver', { placed, left: leftOver }));
    }
  };

  const handleClearSeats = () => {
    const n = Object.keys(draft.seats || {}).length;
    if (n === 0) return;
    if (!window.confirm(t('seatingPlan.clearSeatsConfirm', { count: n }))) return;
    patch(() => ({ seats: {} }));
  };

  // --- Groups editing -------------------------------------------------------------------------
  const [groupForm, setGroupForm] = useState(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [groupPickClass, setGroupPickClass] = useState('');
  const [groupSearch, setGroupSearch] = useState('');

  const groupCandidates = useMemo(() => {
    const term = groupSearch.trim().toLowerCase();
    return students
      .filter(s => !groupPickClass || s.currentClass === groupPickClass)
      .filter(s => !term || s.name.toLowerCase().includes(term) || String(s.sid).includes(term));
  }, [students, groupPickClass, groupSearch]);

  const openNewGroup = () => {
    if (closeSidebar) closeSidebar();
    setGroupPickClass(''); setGroupSearch('');
    setGroupForm({ ...emptyGroup(), id: null });
  };

  const openEditGroup = (g) => {
    if (closeSidebar) closeSidebar();
    setGroupPickClass(g.classCode || ''); setGroupSearch('');
    setGroupForm({ id: g.id, name: g.name || '', classCode: g.classCode || '', students: g.students || [] });
  };

  const toggleGroupStudent = (s) => {
    setGroupForm(prev => {
      const has = prev.students.some(x => x.email === s.email);
      return {
        ...prev,
        students: has
          ? prev.students.filter(x => x.email !== s.email)
          : [...prev.students, { email: s.email, sid: s.sid, name: s.name, currentClass: s.currentClass, classNumber: s.classNumber }],
      };
    });
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (savingGroup || !groupForm?.name.trim()) return;
    setSavingGroup(true);
    try {
      const id = groupForm.id || doc(collection(db, 'schools', activeSchoolId, 'seating_group')).id;
      // Only stamp the author on a new group. Passing undefined to leave it alone does not work --
      // Firestore rejects an undefined value and the save throws, so editing a group never saved.
      const body = {
        name: groupForm.name.trim(),
        classCode: groupPickClass || '',
        students: groupForm.students,
        updatedOn: nowIso(),
        updatedAt: serverTimestamp(),
        updatedByEmail: ownEmail,
      };
      if (!groupForm.id) {
        body.createdByEmail = ownEmail;
        body.createdByName = ownName;
      }
      // merge is right here: `students` is an array, which a merge replaces wholesale, and it keeps
      // the author of a group somebody else made. The plan document is the opposite case -- see the
      // note in handleSave.
      await setDoc(doc(db, 'schools', activeSchoolId, 'seating_group', id), body, { merge: true });
      setGroupForm(null);
    } catch (err) {
      console.error('Error saving group', err);
      alert(t('seatingPlan.saveGroupFailed', { message: err.message }));
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async (g) => {
    const used = plans.filter(p => p.sourceType === 'group' && p.sourceId === g.id);
    const warn = used.length
      ? t('seatingPlan.deleteGroupUsed', { count: used.length })
      : '';
    if (!window.confirm(t('seatingPlan.deleteGroupConfirm', { name: g.name, warning: warn }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'seating_group', g.id));
    } catch (err) {
      console.error('Error deleting group', err);
      alert(t('seatingPlan.deleteGroupFailed', { message: err.message }));
    }
  };

  // --- Render ---------------------------------------------------------------------------------
  const TABS = [
    { id: 'plans', label: t('seatingPlan.tabs.plans'), icon: LayoutGrid },
    { id: 'groups', label: t('seatingPlan.tabs.groups'), icon: Users },
  ];

  const cellStyle = (cell) => {
    const base = {
      borderRadius: 'var(--radius-md)', minHeight: '3.1rem', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '0.1rem', padding: '0.2rem',
      fontSize: '0.68rem', textAlign: 'center', cursor: 'pointer', overflow: 'hidden',
      border: '1px solid var(--color-border)', transition: 'background-color 0.12s ease',
    };
    if (cell.type === CELL.TEACHER) {
      return { ...base, backgroundColor: 'var(--color-primary)', color: 'var(--color-text-on-primary)', borderColor: 'var(--color-primary)', fontWeight: '700' };
    }
    if (cell.type === CELL.BLOCKED) {
      return { ...base, backgroundColor: 'var(--color-bg-app)', borderStyle: 'dashed', color: 'var(--color-text-muted)', cursor: mode === 'room' ? 'pointer' : 'default' };
    }
    if (cell.student) {
      const isPicked = picked?.kind === 'seat' && picked.key === cell.key;
      return {
        ...base,
        backgroundColor: isPicked ? 'var(--color-accent-light)' : 'var(--color-success-bg)',
        borderColor: isPicked ? 'var(--color-accent)' : 'var(--color-success)',
        color: 'var(--color-success-text)', fontWeight: '600',
      };
    }
    return {
      ...base,
      backgroundColor: picked ? 'var(--color-info-bg)' : 'var(--color-bg-card)',
      borderStyle: 'dashed',
      borderColor: picked ? 'var(--color-info)' : 'var(--color-border)',
      color: 'var(--color-text-muted)',
    };
  };

  // The gutter between two columns (or two rows). Wide when it is an aisle, a thin handle while
  // the room is being set up, and nothing at all once you are arranging -- so the finished plan
  // shows the room and not the controls that built it.
  const gutter = (axis, index) => {
    const on = axis === 'col' ? hasColGap(draft, index) : hasRowGap(draft, index);
    const editing = mode === 'room';
    if (!on && !editing) return null;
    const size = on ? AISLE_W : HANDLE_W;
    const vertical = axis === 'col';
    return (
      <button
        type="button"
        onClick={() => editing && patch(p => (axis === 'col'
          ? { colGaps: toggleColGap(p, index) }
          : { rowGaps: toggleRowGap(p, index) }))}
        className={on ? undefined : 'seating-gap-handle'}
        title={editing ? (on ? t('seatingPlan.removeAisle') : t('seatingPlan.putAisleHere')) : t('seatingPlan.aisle')}
        aria-label={on ? t('seatingPlan.aisle') : t('seatingPlan.addAisle')}
        style={{
          flexShrink: 0, padding: 0, border: 'none', borderRadius: '3px',
          width: vertical ? size : 'auto', height: vertical ? 'auto' : size,
          alignSelf: 'stretch',
          cursor: editing ? 'pointer' : 'default',
          backgroundColor: on
            ? 'var(--color-bg-app)'
            : (editing ? 'var(--color-border)' : 'transparent'),
          opacity: on ? 1 : 0.55,
        }}
      />
    );
  };

  const doorSlot = (edge, i) => {
    const on = isDoorAt(draft, edge, i);
    const horizontal = edge === 'top' || edge === 'bottom';
    return (
      <button
        key={`${edge}-${i}`} type="button"
        onClick={() => mode === 'room' && patch(() => ({ door: { edge, index: i } }))}
        title={mode === 'room' ? t('seatingPlan.putDoorHere') : t('seatingPlan.door')}
        style={{
          padding: 0, border: 'none', borderRadius: '3px',
          height: horizontal ? '7px' : 'auto', width: horizontal ? 'auto' : '7px',
          minHeight: horizontal ? '7px' : '1.5rem', minWidth: horizontal ? '1.5rem' : '7px',
          backgroundColor: on ? 'var(--color-accent)' : (mode === 'room' ? 'var(--color-border)' : 'transparent'),
          cursor: mode === 'room' ? 'pointer' : 'default',
        }}
      />
    );
  };

  return (
    <div className="new-theme">
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {TABS.map(tab => (
            <button
              key={tab.id} onClick={() => { setActiveTab(tab.id); setOpenId(null); setDraft(null); }}
              className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`}
              style={{ whiteSpace: 'nowrap' }}
            >
              <span className="theme-tab-icon"><tab.icon size={16} /></span>
              {tab.label}
            </button>
          ))}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {activeTab === 'plans' && !openId && (
              <>
                <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} style={selectStyle}>
                  <option value="">{t('seatingPlan.everyClassAndGroup')}</option>
                  {sources.map(s => (
                    <option key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.label}</option>
                  ))}
                </select>
                <button className="btn-primary" onClick={openNew}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {t('seatingPlan.newPlanButton')}
                </button>
              </>
            )}
            {activeTab === 'groups' && (
              <button className="btn-primary" onClick={openNewGroup}
                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                <Plus size={16} /> {t('seatingPlan.newGroup')}
              </button>
            )}
          </div>
        </HeaderActionsPortal>
      </div>

      {/* ── Plan list ────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'plans' && !openId && (
        <div style={{ ...card(isMobile), flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                  <th style={th}>{t('seatingPlan.plan')}</th>
                  <th style={th}>{t('seatingPlan.classOrGroup')}</th>
                  <th style={th}>{t('seatingPlan.room')}</th>
                  <th style={th}>{t('seatingPlan.seated')}</th>
                  <th style={th}>{t('seatingPlan.updated')}</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {(loadingPlans || loadingRefs) && (
                  <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={6}>{t('common.loading')}</td></tr>
                )}
                {!loadingPlans && shownPlans.length === 0 && (
                  <tr>
                    <td style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem 1rem' }} colSpan={6}>
                      <b style={{ display: 'block', color: 'var(--color-text-main)', fontSize: '0.95rem', marginBottom: '0.25rem' }}>
                        {plans.length === 0 ? t('seatingPlan.noPlanYet') : t('seatingPlan.noPlanForThat')}
                      </b>
                      {t('seatingPlan.listEmptyHint')}
                    </td>
                  </tr>
                )}
                {shownPlans.map(p => {
                  const filled = Object.keys(p.seats || {}).length;
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openPlan(p)}>
                      <td style={{ ...td, fontWeight: '600' }}>{p.name}</td>
                      <td style={td}>
                        <span style={chip}>{p.sourceLabel || p.sourceId}</span>
                      </td>
                      <td style={td}>{p.rows} x {p.cols}</td>
                      <td style={td}>{filled}</td>
                      <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        {String(p.updatedOn || '').slice(0, 10)}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                          <button type="button" onClick={() => handleDuplicate(p)} aria-label={t('seatingPlan.duplicate')} style={{ ...rowBtn, color: 'var(--color-text-subtle)' }}>
                            <Copy size={15} />
                          </button>
                          <button type="button" onClick={() => handleDeletePlan(p)} aria-label={t('common.delete')} style={{ ...rowBtn, color: 'var(--color-danger)' }}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Plan editor ──────────────────────────────────────────────────────────────────── */}
      {activeTab === 'plans' && openId && draft && (
        <div style={{ ...card(isMobile), flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: isMobile ? '0.7rem 0.85rem' : '0.85rem 1.25rem', borderBottom: '1px solid var(--color-border)', display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={closePlan} className="btn-outline" style={{ ...barBtn, padding: '0.4rem 0.6rem' }}>
              <ChevronLeft size={15} /> Plans
            </button>
            <input
              className="input-field" value={draft.name} onChange={e => { const v = e.target.value; patch(() => ({ name: v })); }}
              style={{ width: 'auto', flex: '1 1 12rem', minWidth: 0, padding: '0.4rem 0.6rem', fontSize: '0.9rem', fontWeight: '600' }}
            />
            <span style={chip}>{draft.sourceLabel}</span>

            <div style={{ display: 'flex', gap: '0.3rem', marginLeft: isMobile ? 0 : 'auto' }}>
              {[['arrange', t('seatingPlan.arrangeSeats')], ['room', t('seatingPlan.roomSetup')]].map(([id, label]) => (
                <button
                  key={id} type="button" onClick={() => { setMode(id); setPicked(null); }}
                  style={{
                    padding: '0.35rem 0.8rem', borderRadius: 'var(--radius-md)', fontSize: '0.82rem', cursor: 'pointer',
                    fontWeight: mode === id ? '700' : '500', whiteSpace: 'nowrap',
                    backgroundColor: mode === id ? 'var(--color-nav-active)' : 'transparent',
                    border: `1px solid ${mode === id ? 'var(--color-accent)' : 'var(--color-border)'}`,
                    color: mode === id ? 'var(--color-text-main)' : 'var(--color-text-subtle)',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              type="button" className="btn-outline" onClick={handlePrint} style={barBtn}
              title={t('seatingPlan.printPlan')}
            >
              <Printer size={15} /> Print
            </button>

            <button className="btn-primary" onClick={handleSave} disabled={saving || !dirty} style={barBtn}>
              {saving ? t('common.saving') : dirty ? t('seatingPlan.savePlan') : t('seatingPlan.saved')}
            </button>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: isMobile ? '0.75rem' : '1.25rem', display: 'flex', gap: '1.25rem', flexDirection: isMobile ? 'column' : 'row' }}>
            {/* --- Left rail --- */}
            <div style={{ flex: '0 0 15rem', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {mode === 'room' ? (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {[['rows', 'Rows (front to back)'], ['cols', 'Columns (left to right)']].map(([which, label]) => (
                      <div key={which} style={{ flex: 1, minWidth: 0 }}>
                        <label className="input-label" style={{ fontSize: '0.78rem' }}>{label}</label>
                        <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.25rem' }}>
                          <button
                            type="button" onClick={() => stepSize(which, -1)} aria-label={t('seatingPlan.oneFewer', { which })}
                            disabled={draft[which] <= 1}
                            style={{ ...sizeStep, opacity: draft[which] <= 1 ? 0.4 : 1 }}
                          >-</button>
                          <input
                            className="input-field" type="number" min="1" max="30" inputMode="numeric"
                            value={sizeDraft[which] !== '' ? sizeDraft[which] : draft[which]}
                            onChange={e => setSizeDraft(d => ({ ...d, [which]: e.target.value }))}
                            onBlur={() => commitSize(which)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                              if (e.key === 'Escape') { setSizeDraft(d => ({ ...d, [which]: '' })); e.currentTarget.blur(); }
                            }}
                            style={{ padding: '0.4rem 0.5rem', fontSize: '0.85rem', textAlign: 'center', minWidth: 0 }}
                          />
                          <button
                            type="button" onClick={() => stepSize(which, 1)} aria-label={t('seatingPlan.oneMore', { which })}
                            style={sizeStep}
                          >+</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', marginTop: '-0.35rem' }}>
                    {t('seatingPlan.sizeHint')}
                  </span>

                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-subtle)', lineHeight: 1.7 }}>
                    <b style={{ display: 'block', color: 'var(--color-text-main)', marginBottom: '0.2rem' }}>{t('seatingPlan.clickCell')}</b>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <SquareDashed size={14} /> {t('seatingPlan.seat')}
                      <span style={{ color: 'var(--color-text-muted)' }}>{t('seatingPlan.to')}</span>
                      <Presentation size={14} /> {t('seatingPlan.teacherDesk')}
                      <span style={{ color: 'var(--color-text-muted)' }}>{t('seatingPlan.to')}</span>
                      <X size={14} /> {t('seatingPlan.noSeat')}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.35rem' }}>
                      <DoorOpen size={14} /> {t('seatingPlan.doorHint')}
                    </span>
                  </div>

                  <div style={{ fontSize: '0.8rem', color: 'var(--color-text-subtle)', lineHeight: 1.7 }}>
                    <b style={{ display: 'block', color: 'var(--color-text-main)', marginBottom: '0.2rem' }}>{t('seatingPlan.aisles')}</b>
                    {t('seatingPlan.aislesHint')}
                  </div>

                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button type="button" className="btn-outline" onClick={() => patch(p => ({ colGaps: pairColumns(p.cols) }))} style={barBtn}>
                      {t('seatingPlan.desksInPairs')}
                    </button>
                    <button type="button" className="btn-outline" onClick={() => patch(() => ({ colGaps: noGaps(), rowGaps: noGaps() }))} style={barBtn}>
                      {t('seatingPlan.noAisles')}
                    </button>
                  </div>

                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.7 }}>
                    {t('seatingPlan.blockedHint')}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button type="button" className="btn-outline" onClick={handleAuto} style={barBtn}>
                      <Shuffle size={14} /> {t('seatingPlan.fillEmptySeats')}
                    </button>
                    <button type="button" className="btn-outline" onClick={handleClearSeats} style={{ ...barBtn, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                      <Eraser size={14} /> {t('seatingPlan.emptyAll')}
                    </button>
                  </div>

                  <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                    {picked
                      ? <b style={{ color: 'var(--color-accent)' }}>{t('seatingPlan.nowTapSeat', { name: picked.student?.name })}</b>
                      : t('seatingPlan.tapHint')}
                  </div>

                  {ghostNotice > 0 && (
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', padding: '0.5rem 0.6rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-info-bg)', color: 'var(--color-info-text)', fontSize: '0.78rem' }}>
                      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                      <span>
                        {t('seatingPlan.ghostNotice', { count: ghostNotice })}
                      </span>
                    </div>
                  )}

                  {summary.short > 0 && (
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', padding: '0.5rem 0.6rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', fontSize: '0.78rem' }}>
                      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                      <span>{t('seatingPlan.shortOfSeats', { count: summary.short })}</span>
                    </div>
                  )}

                  {stranded.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', padding: '0.5rem 0.6rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger-text)', fontSize: '0.78rem' }}>
                      <b>{t('seatingPlan.strandedTitle')}</b>
                      {stranded.map(s => (
                        <button key={s.key} type="button" onClick={() => patch(p => ({ seats: clearSeat(p.seats, s.key) }))}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', color: 'inherit', textDecoration: 'underline', fontSize: '0.78rem' }}>
                          {t('seatingPlan.strandedRemove', { name: s.student.name })}
                        </button>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: '0.3rem' }}>
                      {t('seatingPlan.notSeated', { count: unseated.length })}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', maxHeight: isMobile ? '12rem' : '26rem', overflowY: 'auto' }}>
                      {unseated.length === 0 && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                          {roster.length === 0 ? t('seatingPlan.nobodyInIt') : t('seatingPlan.everybodySeated')}
                        </span>
                      )}
                      {unseated.map(s => {
                        const on = picked?.kind === 'student' && picked.student.email === s.email;
                        return (
                          <button
                            key={s.email} type="button" draggable
                            onDragStart={() => takeStudent(s)}
                            onClick={() => setPicked(on ? null : { kind: 'student', student: s })}
                            style={{
                              display: 'flex', gap: '0.45rem', alignItems: 'center', textAlign: 'left',
                              padding: '0.3rem 0.5rem', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                              fontSize: '0.82rem',
                              border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
                              backgroundColor: on ? 'var(--color-accent-light)' : 'var(--color-bg-card)',
                            }}
                          >
                            <span style={{ color: 'var(--color-text-muted)', minWidth: '1.6rem' }}>{s.classNumber || ''}</span>
                            <span style={{ fontWeight: '600' }}>{s.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* --- The room --- */}
            <div className="seating-print-area" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {/* On paper the plan has to say what it is: the screen around it is gone, and a chart
                  with no class or date on it is the one that ends up pinned to the wrong door. */}
              <div className="seating-print-only" style={{ marginBottom: '0.6rem' }}>
                <div style={{ fontSize: '1.15rem', fontWeight: '700' }}>{draft.name}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-subtle)' }}>
                  {draft.sourceLabel}
                  {' · '}{summary.filled} of {summary.seats} seats
                  {' · '}printed {new Date().toLocaleDateString('en-CA')}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                  {t('seatingPlan.frontOfRoom')}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                  {summary.filled} of {summary.seats} seats filled
                </span>
              </div>

              {/* The wall strips are woven with the same gutters, or the door stops lining up with
                  the column it is meant to be beside. */}
              <div style={{ display: 'flex', gap: '0.3rem', paddingLeft: '11px', paddingRight: '11px' }}>
                {Array.from({ length: doorSlots(draft, 'top') }, (_, i) => (
                  <React.Fragment key={i}>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>{doorSlot('top', i)}</div>
                    {i < draft.cols - 1 && hasColGap(draft, i) && <div style={{ width: AISLE_W, flexShrink: 0 }} />}
                    {i < draft.cols - 1 && !hasColGap(draft, i) && mode === 'room' && <div style={{ width: HANDLE_W, flexShrink: 0 }} />}
                  </React.Fragment>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '4px', alignItems: 'stretch' }}>
                {/* Left wall */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', justifyContent: 'stretch' }}>
                  {Array.from({ length: doorSlots(draft, 'left') }, (_, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>{doorSlot('left', i)}</div>
                  ))}
                </div>

                {/* Rows of cells with the gutters woven in, rather than one CSS grid. An aisle is
                    narrower than a desk, so it cannot be a grid column -- and every cell being
                    `flex: 1 1 0` keeps the columns lined up across rows regardless of the gaps. */}
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  {grid.map((line, r) => (
                    <React.Fragment key={`r${r}`}>
                      <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'stretch' }}>
                        {line.map((cell, c) => (
                          <React.Fragment key={cell.key}>
                            <div
                              onClick={() => (mode === 'room' ? cycleCell(cell.row, cell.col) : (cell.type === CELL.SEAT && dropOn(cell.key)))}
                              onDragOver={mode === 'arrange' && cell.type === CELL.SEAT ? (e) => e.preventDefault() : undefined}
                              onDrop={mode === 'arrange' && cell.type === CELL.SEAT ? (e) => { e.preventDefault(); dropOn(cell.key); } : undefined}
                              draggable={mode === 'arrange' && !!cell.student}
                              onDragStart={cell.student ? () => takeSeat(cell.key) : undefined}
                              style={{ ...cellStyle(cell), flex: '1 1 0', minWidth: 0 }}
                              title={cell.student ? cell.student.name : ''}
                            >
                              {cell.type === CELL.TEACHER && <><Presentation size={15} /><span>{t('seatingPlan.teacher')}</span></>}
                              {cell.type === CELL.BLOCKED && <X size={13} />}
                              {cell.type === CELL.SEAT && cell.student && (
                                <>
                                  <span style={{ fontWeight: '700' }}>{cell.student.classNumber || ''}</span>
                                  <span style={{ lineHeight: 1.15, wordBreak: 'break-word' }}>{cell.student.name}</span>
                                </>
                              )}
                              {cell.type === CELL.SEAT && !cell.student && <Armchair size={14} />}
                            </div>
                            {c < draft.cols - 1 && gutter('col', c)}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* A row gap: the walkway across the room, in front of the back block. */}
                      <div style={{ height: '0.3rem' }} />
                      {r < draft.rows - 1 && (
                        <div style={{ display: 'flex' }}>{gutter('row', r)}</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Right wall */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {Array.from({ length: doorSlots(draft, 'right') }, (_, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>{doorSlot('right', i)}</div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.3rem', paddingLeft: '11px', paddingRight: '11px' }}>
                {Array.from({ length: doorSlots(draft, 'bottom') }, (_, i) => (
                  <React.Fragment key={i}>
                    <div style={{ flex: '1 1 0', minWidth: 0 }}>{doorSlot('bottom', i)}</div>
                    {i < draft.cols - 1 && hasColGap(draft, i) && <div style={{ width: AISLE_W, flexShrink: 0 }} />}
                    {i < draft.cols - 1 && !hasColGap(draft, i) && mode === 'room' && <div style={{ width: HANDLE_W, flexShrink: 0 }} />}
                  </React.Fragment>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                <DoorOpen size={13} style={{ color: 'var(--color-accent)' }} />
                Door: {draft.door?.edge || 'not set'}
                {mode === 'room' ? ' -- click any wall segment to move it' : ''}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Groups ──────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'groups' && (
        <div style={{ ...card(isMobile), flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: isMobile ? '0.75rem' : '1rem 1.25rem', borderBottom: '1px solid var(--color-border)', fontSize: '0.82rem', color: 'var(--color-text-muted)', lineHeight: 1.7 }}>
            {t('seatingPlan.groupsIntro')}
            {t('seatingPlan.groupsWhy')}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                  <th style={th}>{t('seatingPlan.group')}</th>
                  <th style={th}>{t('seatingPlan.drawnFrom')}</th>
                  <th style={th}>{t('seatingPlan.students')}</th>
                  <th style={th}>{t('seatingPlan.plans')}</th>
                  <th style={th} />
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 && (
                  <tr>
                    <td style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem 1rem' }} colSpan={5}>
                      <b style={{ display: 'block', color: 'var(--color-text-main)', fontSize: '0.95rem', marginBottom: '0.25rem' }}>{t('seatingPlan.noGroupYet')}</b>
                      {t('seatingPlan.noGroupHint')}
                    </td>
                  </tr>
                )}
                {groups.map(g => (
                  <tr key={g.id}>
                    <td style={{ ...td, fontWeight: '600' }}>{g.name}</td>
                    <td style={td}>{g.classCode ? <span style={chip}>{g.classCode}</span> : <span style={{ color: 'var(--color-text-muted)' }}>mixed</span>}</td>
                    <td style={td}>{(g.students || []).length}</td>
                    <td style={td}>{plans.filter(p => p.sourceType === 'group' && p.sourceId === g.id).length}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.3rem' }}>
                        <button type="button" onClick={() => openEditGroup(g)} aria-label={t('common.edit')} style={{ ...rowBtn, color: 'var(--color-primary)' }}>
                          <Pencil size={15} />
                        </button>
                        <button type="button" onClick={() => handleDeleteGroup(g)} aria-label={t('common.delete')} style={{ ...rowBtn, color: 'var(--color-danger)' }}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── New plan dialog ─────────────────────────────────────────────────────────────── */}
      {newForm && (
        <div onClick={() => { if (!creating) setNewForm(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '540px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('seatingPlan.newPlan')}</h3>
              <button type="button" onClick={() => setNewForm(null)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreate} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label className="input-label">{t('seatingPlan.classOrGroup')}</label>
                <select className="input-field" value={newForm.sourceKey} required
                  onChange={e => setNewForm(p => ({ ...p, sourceKey: e.target.value }))}>
                  <option value="">{t('seatingPlan.choose')}</option>
                  {sources.map(s => <option key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.label}</option>)}
                </select>
              </div>

              <div>
                <label className="input-label">{t('seatingPlan.nameLabel')}</label>
                <input className="input-field" value={newForm.name}
                  onChange={e => setNewForm(p => ({ ...p, name: e.target.value }))}
                  placeholder={t('seatingPlan.nameHint')} />
              </div>

              <div>
                <label className="input-label">{t('seatingPlan.roomSize')}</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input className="input-field" type="number" min="1" max="20" value={newForm.rows}
                    onChange={e => setNewForm(p => ({ ...p, rows: e.target.value }))} style={{ maxWidth: '6rem' }} />
                  <span style={{ color: 'var(--color-text-muted)' }}>rows by</span>
                  <input className="input-field" type="number" min="1" max="20" value={newForm.cols}
                    onChange={e => setNewForm(p => ({ ...p, cols: e.target.value }))} style={{ maxWidth: '6rem' }} />
                  <span style={{ color: 'var(--color-text-muted)' }}>columns</span>
                </div>
                <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                  {(Number(newForm.rows) || 0) * (Number(newForm.cols) || 0)} seats to start with. You can
                  change the size, block out cells and place the teacher desk afterwards.
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setNewForm(null)} disabled={creating} style={modalBtn}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={creating || !newForm.sourceKey} style={modalBtn}>
                  {creating ? t('seatingPlan.creating') : t('seatingPlan.createAndArrange')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Group dialog ────────────────────────────────────────────────────────────────── */}
      {groupForm && (
        <div onClick={() => { if (!savingGroup) setGroupForm(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '620px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{groupForm.id ? t('seatingPlan.editGroup') : t('seatingPlan.newGroup')}</h3>
              <button type="button" onClick={() => setGroupForm(null)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveGroup} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label className="input-label">{t('seatingPlan.groupName')}</label>
                <input className="input-field" value={groupForm.name} required
                  onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))}
                  placeholder={t('seatingPlan.groupNamePlaceholder')} />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 9rem' }}>
                  <label className="input-label">{t('seatingPlan.pickFrom')}</label>
                  <select className="input-field" value={groupPickClass} onChange={e => setGroupPickClass(e.target.value)}>
                    <option value="">{t('seatingPlan.everyClass')}</option>
                    {classes.map(c => <option key={c.code} value={c.code}>{c.name || c.code}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 9rem' }}>
                  <label className="input-label">{t('seatingPlan.search')}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Search size={15} style={{ color: 'var(--color-text-muted)' }} />
                    <input className="input-field" value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder={t('seatingPlan.searchPlaceholder')} />
                  </div>
                </div>
              </div>

              <div>
                <label className="input-label">Students ({groupForm.students.length} chosen)</label>
                <div style={{ maxHeight: '15rem', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                  {groupCandidates.length === 0 && (
                    <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{t('seatingPlan.nobodyMatches')}</span>
                  )}
                  {groupCandidates.map(s => (
                    <label key={s.email} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', padding: '0.15rem' }}>
                      <input type="checkbox" checked={groupForm.students.some(x => x.email === s.email)} onChange={() => toggleGroupStudent(s)} />
                      <span style={{ minWidth: '3.5rem', color: 'var(--color-text-subtle)' }}>
                        {[s.currentClass, s.classNumber].filter(Boolean).join('-')}
                      </span>
                      <span style={{ fontWeight: '600' }}>{s.name}</span>
                    </label>
                  ))}
                </div>
                {/* Chosen students from other classes stay chosen while the filter changes -- the
                    filter narrows what you can SEE, never what you have already picked. */}
                {groupForm.students.some(s => groupPickClass && s.currentClass !== groupPickClass) && (
                  <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                    {t('seatingPlan.othersChosen')}
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setGroupForm(null)} disabled={savingGroup} style={modalBtn}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={savingGroup || !groupForm.name.trim()} style={modalBtn}>
                  {savingGroup ? t('common.saving') : t('seatingPlan.saveGroup')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatingPlan;
